import { Autocomplete, Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Switch, TextField, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { LoadingButton } from '@mui/lab';
import { ContentCopy, DoneAll, RemoveDone } from "@mui/icons-material";
import $api from "../http";
import { useTranslation } from "react-i18next";
import { useAppConext } from "../context/AppContext";
import { useSnackbar } from "notistack";

const WidgetCopyDialog = (props) => {
    const { t } = useTranslation();
    const {open, onSuccess, onClose, uid, currentItem} = props;
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState({
        general: 1,
        button: 1,
        menu: 1,
        integration: 1,
        forms: 1,
        email: 1,
        prompts: 1,
        livechat: 1,
        menuitems: 1
    });
    const [currentWidgetUid, setCurrentWidgetUid] = useState();
    const [items, setItems] = useState([]);
    const [newValue, setNewValue] = useState(null);
    const [defaultItem, setDefaultItem] = useState(null);
    const { enqueueSnackbar } = useSnackbar();

    const { user, getFeatureValue } = useAppConext();

    const handleDialogClose = () => {
        setIsOpen(false);
        onClose();
    };

    const handleDialogSubmit = () => {
        setIsLoading(true);
        $api.post('/widget/copy', {
            ...data,
            uid: currentWidgetUid,
            user_id: newValue?.id
        })
            .catch((err) => {
                enqueueSnackbar(t('Error copying widget'), {
                    variant: 'error'
                });
                return false;
            })
            .then((res) => {
                if (res) {
                    onSuccess();
                    onClose();
                }
            })
            .finally(() => {
                setIsLoading(false);
            })
    };

    const handleFormFieldChange = (e) => {
        const {name, checked} = e.target;
        setData({...data, [name]: checked ? 1 : 0});
    };

    const handleCheckAll = () => {
        setData({
            general: 1,
            button: 1,
            menu: 1,
            integration: 1,
            forms: 1,
            email: 1,
            prompts: 1,
            livechat: 1,
            menuitems: 1
        });
    };

    const handleUncheckAll = () => {
        setData({
            general: 0,
            button: 0,
            menu: 0,
            integration: 0,
            forms: 0,
            email: 0,
            prompts: 0,
            livechat: 0,
            menuitems: 0
        });
    };

    const loadContent = () => {
        $api.get('/user/get-clients')
            .catch((err) => {

            })
            .then((res) => {
                if (res) {
                    setItems([
                        {
                            id: user.id,
                            name: user.name,
                            email: user.email,
                            image: user.image
                        },
                        ...res.data
                    ]);
                }
            })
            .finally(() => {
                setIsLoading(false);
            })
    };

    const handleChange = (e, v) => {
        setNewValue(v);
    };

    useEffect(() => {
        const d = items.filter((i) => i.id === currentItem.user_id);
        if (d.length === 1) {
            setDefaultItem(d[0]);
            setNewValue(d[0]);
        }
    }, [items]);

    useEffect(() => {
        setIsOpen(open);
        if (open) {
            loadContent();
        }
    }, [open]);

    useEffect(() => {
        setCurrentWidgetUid(uid);
    }, [uid]);

    return (
        <Dialog fullWidth maxWidth="sm" open={isOpen} onClose={handleDialogClose}>
            <DialogTitle>
                <Box sx={{ display: 'flex' }}>
                    <Box sx={{ my: 'auto' }}>
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: '500' }}>
                            {t('Copy widget')}
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                        <Button startIcon={<DoneAll />} size="small" sx={{ mr: 1 }} variant="outlined" onClick={handleCheckAll}>
                            {t('Check all')}
                        </Button>
                        <Button startIcon={<RemoveDone />} size="small" variant="outlined" onClick={handleUncheckAll}>
                            {t('Uncheck all')}
                        </Button>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent>
                {(getFeatureValue('agency.enabled') === true) && (
                    <FormGroup sx={{ mt: 2 }}>
                        <Autocomplete
                            sx={{ mt: 2 }}
                            options={items}
                            autoHighlight
                            fullWidth
                            getOptionLabel={(i) => i.name}
                            onChange={handleChange}
                            value={newValue || defaultItem}
                            renderOption={(props, i) => (
                                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                    <Avatar src={i.image} sx={{ mr: 1 }} />
                                    <Box>
                                        <Typography>
                                            {i.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {i.email}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    label={t('Target account')}
                                    inputProps={{
                                        ...params.inputProps,
                                        autoComplete: 'new-password', // disable autocomplete and autofill
                                    }}
                                />
                            )}
                        />
                    </FormGroup>
                )}
                
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={<Switch checked={!!data.menuitems} onChange={handleFormFieldChange} name="menuitems" />}
                        label={t('Copy channels')}
                    />
                </Box>
                <Box>
                    <FormControlLabel
                        control={<Switch checked={!!data.button} onChange={handleFormFieldChange} name="button" />}
                        label={t('Copy button settings')}
                    />
                </Box>
                <Box>
                    <FormControlLabel
                        control={<Switch checked={!!data.menu} onChange={handleFormFieldChange} name="menu" />}
                        label={t('Copy menu settings')}
                    />
                </Box>
                <Box>
                    <FormControlLabel
                        control={<Switch checked={!!data.prompts} onChange={handleFormFieldChange} name="prompts" />}
                        label={t('Copy prompt messages')}
                    />
                </Box>
                <Box>
                    <FormControlLabel
                        control={<Switch checked={!!data.livechat} onChange={handleFormFieldChange} name="livechat" />}
                        label={t('Copy live chat settings')}
                    />
                </Box>
                <Box>
                    <FormControlLabel
                        control={<Switch checked={!!data.general} onChange={handleFormFieldChange} name="general" />}
                        label={t('Copy advanced settings')}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={handleDialogClose}>
                    {t('Close')}
                </Button>
                <LoadingButton onClick={handleDialogSubmit} startIcon={<ContentCopy />} color="primary" variant="contained" loadingPosition="start" loading={isLoading}>
                    {t('Copy')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default WidgetCopyDialog;